// The band above a code block, with the language name and a copy button.
//
// The editor (node view) and the static view draw the same DOM:
// `div.trameria-code > div.trameria-code-bar (language + button) + pre`. With
// different shapes, the body would move the moment the editor takes over.
//
// The button sits in the band, not over the code: long lines' right ends hid
// under it ("that does bother me"; the band shows on phones too). It is
// outside `pre` because `pre` scrolls sideways (inside, it would scroll with
// the code). The wrapper carries `data-block-id` (block hit areas and drags
// look at the outermost DOM).
//
// Code blocks of the language `ui` are drawn by the ui fence's view when the
// document lists that part (block-parts.ts); its DOM starts from this band.
import type { NodeViewRenderer } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { BLOCK_ID_DOM_ATTR, blockIdDomAttrs } from "@trameria/editor";
import { m } from "@trameria/i18n";
import { copyText } from "./copy";

export const CODE_BLOCK_CLASS = "trameria-code";
export const CODE_BAR_CLASS = "trameria-code-bar";
export const CODE_LANG_CLASS = "trameria-code-lang";
export const CODE_COPY_CLASS = "trameria-code-copy";

/** The copy icon, as a string so the static view's HTML can draw it too. */
const COPY_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" class="pointer-events-none">' +
  '<rect x="5.5" y="5.5" width="8" height="8" rx="1.6" stroke="currentColor" stroke-width="1.25"></rect>' +
  '<path d="M10.5 3.2V3.1c0-.9-.7-1.6-1.6-1.6H4.1c-.9 0-1.6.7-1.6 1.6v4.8c0 .9.7 1.6 1.6 1.6h.2" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"></path>' +
  "</svg>";

/** The language from `code`'s `language-xxx` class ("" without one). */
export function codeLanguage(className: string | null | undefined): string {
  return /(?:^|\s)language-(\S+)/.exec(className ?? "")?.[1] ?? "";
}

/** The band. The language holder is there even when empty (it pushes the button right). */
export function codeBar(
  doc: Document,
  lang: string,
): { bar: HTMLElement; label: HTMLElement; button: HTMLButtonElement } {
  const bar = doc.createElement("div");
  bar.className = CODE_BAR_CLASS;
  const label = doc.createElement("span");
  label.className = CODE_LANG_CLASS;
  label.textContent = lang;
  const button = doc.createElement("button");
  button.type = "button";
  button.className = CODE_COPY_CLASS;
  button.setAttribute("aria-label", m.blocks_code_copy());
  button.innerHTML = COPY_ICON_SVG;
  bar.append(label, button);
  return { bar, label, button };
}

/**
 * Wraps the static view's plain code blocks and adds the band. trameria's
 * fences (mermaid: `pre[data-trameria-fence]`) are another part in the editor,
 * so they are not wrapped.
 */
export function wrapCodeBlocks(root: HTMLElement): void {
  for (const pre of root.querySelectorAll<HTMLElement>(
    "pre:not([data-trameria-fence])",
  )) {
    const doc = pre.ownerDocument;
    const box = doc.createElement("div");
    box.className = CODE_BLOCK_CLASS;
    const blockId = pre.getAttribute(BLOCK_ID_DOM_ATTR);
    if (blockId) {
      box.setAttribute(BLOCK_ID_DOM_ATTR, blockId);
      pre.removeAttribute(BLOCK_ID_DOM_ATTR);
    }
    const lang = codeLanguage(pre.querySelector("code")?.className);
    pre.replaceWith(box);
    box.append(codeBar(doc, lang).bar, pre);
  }
}

/**
 * The static view's copy button copies its code. One delegated listener on the
 * wrapper (not one per button).
 */
export function copyCodeBlock(
  root: HTMLElement,
  target: EventTarget | null,
): boolean {
  if (!(target instanceof Element)) return false;
  const button = target.closest(`button.${CODE_COPY_CLASS}`);
  if (!button || !root.contains(button)) return false;
  const pre = button.closest(`.${CODE_BLOCK_CLASS}`)?.querySelector("pre");
  if (!pre) return false;
  void copyText(pre.textContent ?? "");
  return true;
}

function nodeLanguage(node: PMNode): string {
  const lang = node.attrs.language;
  return typeof lang === "string" ? lang : "";
}

function setLanguage(code: HTMLElement, label: HTMLElement, node: PMNode) {
  const lang = nodeLanguage(node);
  if (lang) code.className = `language-${lang}`;
  else code.removeAttribute("class");
  if (label.textContent !== lang) label.textContent = lang;
}

/** Puts the node's block id on its outermost DOM (or takes it off). */
export function setBlockId(dom: HTMLElement, node: PMNode): void {
  const blockId = blockIdDomAttrs(node)[BLOCK_ID_DOM_ATTR];
  if (blockId) dom.setAttribute(BLOCK_ID_DOM_ATTR, blockId);
  else dom.removeAttribute(BLOCK_ID_DOM_ATTR);
}

/**
 * The editor's `codeBlock` node view (the same shape as above).
 *
 * The band is outside editing (`contenteditable=false`): `stopEvent` keeps it
 * from ProseMirror and `mousedown` is prevented so the selection and focus
 * stay. The button is not in the Tab order (it is inside the editor). What is
 * copied is the node's text, not the DOM (independent of highlight spans).
 */
export const codeBlockCopyView: NodeViewRenderer = ({ node: initial }) => {
  let node = initial;
  const dom = document.createElement("div");
  dom.className = CODE_BLOCK_CLASS;
  const { bar, label, button } = codeBar(document, nodeLanguage(node));
  bar.contentEditable = "false";
  bar.addEventListener("mousedown", (e) => e.preventDefault());
  button.tabIndex = -1;
  button.addEventListener("click", () => void copyText(node.textContent));
  const pre = document.createElement("pre");
  const code = document.createElement("code");
  pre.append(code);
  dom.append(bar, pre);
  setLanguage(code, label, node);
  setBlockId(dom, node);
  return {
    dom,
    contentDOM: code,
    update(next) {
      if (next.type !== node.type) return false;
      node = next;
      setLanguage(code, label, node);
      setBlockId(dom, node);
      return true;
    },
    stopEvent: (e) => e.target instanceof Node && bar.contains(e.target),
    ignoreMutation: (mutation) =>
      mutation.type !== "selection" &&
      (bar.contains(mutation.target) ||
        (mutation.type === "attributes" &&
          (mutation.target === dom || mutation.target === code))),
  };
};
